/**
 * Weapon-FX prefab types (W4 of the voxel-cosmetics plan).
 *
 * Each `WeaponFxSpawn` is one engine `weapon_fire` event translated
 * into render-space coordinates. `<WeaponFxField>` accepts a list and
 * dispatches each to the matching prefab. Auto-prunes once a spawn's
 * lifetime elapses.
 *
 * Plan reference: docs/dev/visual-engine/voxel-cosmetics-plan.md
 * §4 (W4 component inventory) + §1 (weapon FX exception — these are
 * parameterized particle systems, not VOX assets).
 */

import type { Damage } from "../../replay/events";

/** Engine-space coordinate (world position). */
export type World3 = [number, number, number];

/** Discriminated kind that maps `weapon_fire` events → prefab choice. */
export type WeaponFxKind =
  | "ballistic_tracer"
  | "energy_beam"
  | "missile_arc"
  | "explosion_burst"
  | "muzzle_flash"
  | "shockwave_ring";

export interface WeaponFxSpawn {
  /** Stable per-event id — typically `tick * 1000 + seq`. */
  id: number;
  kind: WeaponFxKind;
  origin: World3;
  target: World3;
  /** Damage type for palette / scale selection. */
  damageType: Damage;
  /** 32-bit seed mixed into per-prefab variation. Replay-deterministic. */
  seed: number;
  /** Engine tick that spawned this FX (for debug + analytics). */
  tick: number;
  /** When true, render with a desaturated palette + slight overshoot —
   *  the shot missed. Sourced from raw `weapon_dud` events. */
  miss?: boolean;
}

/**
 * Pick a `WeaponFxKind` for the given `damageType`. Centralized so the
 * mapping doesn't drift between ReplayScene and individual story
 * test cases.
 */
export function defaultFxKindForDamage(damageType: Damage): WeaponFxKind {
  switch (damageType) {
    case "ballistic":
      return "ballistic_tracer";
    case "energy":
      return "energy_beam";
    case "missile":
      return "missile_arc";
    case "explosive":
      return "explosion_burst";
    default:
      return "ballistic_tracer";
  }
}
